import {
  Badge,
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Announcement, AnnouncementRecipient } from "core";
import { httpsCallable } from "firebase/functions";
import React, { useEffect, useState } from "react";
import { useFunctions } from "reactfire";

interface AnnouncementDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  announcement: Announcement & { id: string };
}

type RecipientWithId = AnnouncementRecipient & { id: string };

export default function AnnouncementDetailModal({
  isOpen,
  onClose,
  announcement,
}: AnnouncementDetailModalProps) {
  const functions = useFunctions();
  const [recipients, setRecipients] = useState<RecipientWithId[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchRecipients() {
      if (!announcement.id || !isOpen) return;

      try {
        setLoading(true);
        const getAnnouncementFn = httpsCallable(functions, "getAnnouncement");
        const result = await getAnnouncementFn({
          announcementId: announcement.id,
          includeRecipients: true,
        });

        const data = result.data as {
          success: boolean;
          announcement: Announcement & { id: string };
          recipients: RecipientWithId[];
        };

        setRecipients(data.recipients || []);
      } catch (error) {
        console.error("Error fetching announcement details:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchRecipients();
  }, [announcement.id, isOpen, functions]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "sent":
        return "green";
      case "pending":
        return "orange";
      case "failed":
        return "red";
      default:
        return "gray";
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const progress =
    announcement.totalRecipients > 0
      ? (announcement.sentCount / announcement.totalRecipients) * 100
      : 0;

  const pendingCount = recipients.filter((r) => r.status === "pending").length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Announcement Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <VStack spacing={6} align="stretch">
            {/* Summary Section */}
            <Box>
              <Heading size="sm" mb={2}>
                Summary
              </Heading>
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="medium">Status:</Text>
                  <Badge colorScheme={getStatusColor(announcement.status)} fontSize="md">
                    {announcement.status}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Created:</Text>
                  <Text>{formatDate(announcement.createdAt)}</Text>
                </HStack>
                {announcement.completedAt && (
                  <HStack justify="space-between">
                    <Text fontWeight="medium">Completed:</Text>
                    <Text>{formatDate(announcement.completedAt)}</Text>
                  </HStack>
                )}
                <HStack justify="space-between">
                  <Text fontWeight="medium">Total Recipients:</Text>
                  <Text>{announcement.totalRecipients}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Progress:</Text>
                  <Box flex={1} maxW="300px">
                    <HStack spacing={2} mb={1}>
                      <Text fontSize="sm" color="green.600">
                        {announcement.sentCount} sent
                      </Text>
                      {pendingCount > 0 && (
                        <Text fontSize="sm" color="orange.600">
                          {pendingCount} pending
                        </Text>
                      )}
                      {announcement.failedCount > 0 && (
                        <Text fontSize="sm" color="red.600">
                          {announcement.failedCount} failed
                        </Text>
                      )}
                    </HStack>
                    <Progress
                      value={progress}
                      size="sm"
                      colorScheme={
                        announcement.status === "completed" ? "green" : "blue"
                      }
                      borderRadius="md"
                    />
                  </Box>
                </HStack>
                {announcement.errorMessage && (
                  <Box p={3} bg="red.50" borderRadius="md">
                    <Text fontWeight="bold" color="red.700" mb={1}>
                      Error:
                    </Text>
                    <Text fontSize="sm" color="red.600">
                      {announcement.errorMessage}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>

            <Divider />

            {/* Message Section */}
            <Box>
              <Heading size="sm" mb={2}>
                Message Template
              </Heading>
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text whiteSpace="pre-wrap">{announcement.message}</Text>
              </Box>
            </Box>

            <Divider />

            {/* Recipients Section */}
            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <Heading size="sm">Recipients</Heading>
                {loading && <Spinner size="sm" />}
              </Flex>

              {loading ? (
                <Flex justify="center" p={8}>
                  <Spinner />
                </Flex>
              ) : recipients.length === 0 ? (
                <Text color="gray.600">No recipients found</Text>
              ) : (
                <Box borderWidth={1} borderRadius="lg" overflow="hidden">
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>User</Th>
                        <Th>Phone</Th>
                        <Th>Status</Th>
                        <Th>Sent At</Th>
                        <Th>Message</Th>
                        <Th>Error</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {recipients.map((recipient) => (
                        <Tr key={recipient.id}>
                          <Td>
                            <Text fontSize="sm">{recipient.userName || "—"}</Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm" fontFamily="mono">
                              {recipient.userPhoneNumber || "—"}
                            </Text>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(recipient.status)}>
                              {recipient.status}
                            </Badge>
                          </Td>
                          <Td>
                            <Text fontSize="sm">
                              {formatDate(recipient.sentAt)}
                            </Text>
                          </Td>
                          <Td maxW="300px">
                            <Text fontSize="sm" noOfLines={2}>
                              {recipient.interpolatedMessage}
                            </Text>
                          </Td>
                          <Td maxW="200px">
                            {recipient.errorMessage && (
                              <Text fontSize="xs" color="red.600" noOfLines={2}>
                                {recipient.errorMessage}
                              </Text>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
